import { Box, CircularProgress, Typography } from '@mui/material';
import React, { useEffect, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useUser } from '../context/UserContext.tsx';

interface AuthWrapperProps {
  children: React.ReactNode;
  requireAuth: boolean;
}

const AuthWrapper: React.FC<AuthWrapperProps> = ({ children, requireAuth }) => {
  const { loading: isLoading, user } = useUser();
  const [authChecked, setAuthChecked] = useState(false);
  const location = useLocation();

  useEffect(() => {
    // Once loading is complete, mark auth as checked
    if (!isLoading) {
      setAuthChecked(true);
    }
  }, [isLoading]);

  // Show loading indicator while checking auth
  if (isLoading || !authChecked) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '100vh',
        }}
      >
        <CircularProgress size={60} />
        <Typography variant="h6" sx={{ mt: 2 }}>
          Checking authentication...
        </Typography>
      </Box>
    );
  }

  // Handle protected routes
  if (requireAuth && !user) {
    // Redirect to landing page if authentication is required but user is not authenticated
    return <Navigate to="/" replace state={{ from: location }} />;
  }

  // Handle already authenticated users trying to access public auth pages
  if (!requireAuth && user && (location.pathname === '/login' || location.pathname === '/signup')) {
    // Redirect to app if already authenticated
    return <Navigate to="/app" replace />;
  }

  // Render children when auth check passes
  return <>{children}</>;
};

export default AuthWrapper; 